import mixpanel from "mixpanel-browser";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { styled } from "styled-components";
import { routeList } from "../../App";
import { lang } from "../../assets/languages/en.language";
import { AgeRangeInput } from "../../components/form/AboutYouConnectWith/AgeRange.input";
import { BirthDateInput } from "../../components/form/AboutYouConnectWith/BirthDate.input";
import { MasterTypeInput } from "../../components/form/AboutYouConnectWith/MasterType.input";
import { MasterTypesInput } from "../../components/form/AboutYouConnectWith/MasterTypes.input";
import { ManWalkDog } from "../../components/svgs/illustrations/GirlWalkDog";
import { usePwaViewportWidth } from "../../hooks/useScreenWidth";
import api from "../../services/api";
import { getVpFontSize } from "../../services/utils";
import { useUserStore } from "../../store/useUserStore";
import { PurpleButton } from "../../styles/Button.style";
import { MainContainer, RootContainer } from "../../styles/Container.style";
import { ErrorMessage, StyledForm } from "../../styles/Form";
import { BigTitle, Title as MainCompTitle } from "../../styles/Text";
import { Gap } from "../../styles/Utils";
import { IFormInput } from "../onboarding/AboutYouConnectWith.page";

export default function PicturePage() {
  const navigate = useNavigate();
  const viewportWidth = usePwaViewportWidth();

  const {
    setAboutYouConnectWith,
    profile: { birthDate, youRole, themRole, ageFrom, ageTo },
  } = useUserStore();

  const methods = useForm<IFormInput>({
    mode: "onChange",
    defaultValues: {
      birthDate: birthDate ?? undefined,
      youRole: youRole ?? undefined,
      themRole: themRole ?? undefined,
      ageFrom: ageFrom ?? undefined,
      ageTo: ageTo ?? undefined,
    },
  });

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isValid },
  } = methods;

  const onSubmit = async (data: IFormInput) => {
    const { ageFrom, ageTo } = data;
    const payload = {
      ...data,
      ageFrom: ageFrom as number,
      ageTo: ageTo as number,
    };
    try {
      await api.updateAboutYouConnectWith(payload);
      mixpanel.track("Update About You Connect With");
      setAboutYouConnectWith(payload);
      navigate(routeList.profile);
    } catch (err) {
      console.log("Error updating <you connect with> infos", err);
      alert("Error updating <you connect with> infos");
    }
  };

  return (
    <RootContainer>
      <BigTitle $fontSize={getVpFontSize(viewportWidth, 10)}>{lang.updateAboutYouConnectWithPage.title}</BigTitle>
      <Gap $height={1} />
      <MainContainer>
        <FormProvider {...methods}>
          <StyledForm>
            <MainCompTitle>{lang.addYouConnectWithPage.title}</MainCompTitle>
            <Gap $height={1} />
            <BirthDateInput register={register} name="birthDate" />
            {errors.birthDate && <ErrorMessage>{errors.birthDate.message}</ErrorMessage>}
            <Gap $height={1} />
            <MasterTypeInput who="You are" register={register} name="youRole" />
            <Gap $height={1} />
            <MainCompTitle>{lang.addYouConnectWithPage.title2}</MainCompTitle>
            <Gap $height={1} />
            <Controller
              name="ageFrom"
              control={control}
              rules={{ required: "L'âge de départ est requis" }}
              render={({ field }) => (
                <Controller
                  name="ageTo"
                  control={control}
                  rules={{ required: "L'âge de fin est requis" }}
                  render={({ field: field2 }) => (
                    <AgeRangeInput
                      value1={field.value}
                      value2={field2.value}
                      onChange1={field.onChange}
                      onChange2={field2.onChange}
                    />
                  )}
                />
              )}
            />
            <Gap $height={1} />
            <Controller
              name="themRole"
              control={control}
              rules={{ required: "This field is required" }} // Enforce required validation
              render={({ field }) => <MasterTypesInput who="Them" value={field.value} onChange={field.onChange} />}
            />
          </StyledForm>
        </FormProvider>
        <IllustrationWrapper $viewportWidth={viewportWidth}>
          <ManWalkDog />
        </IllustrationWrapper>
      </MainContainer>
      <Gap $height={1.5} />

      <PurpleButton type="submit" disabled={!isValid} onClick={handleSubmit(onSubmit)}>
        <span>{lang.buttons.continue}</span>
      </PurpleButton>
    </RootContainer>
  );
}

const IllustrationWrapper = styled.div<{ $viewportWidth: number }>`
  width: 100%;
  position: relative;
  height: ${(props) => (props.$viewportWidth * 50) / 100 + "px"};
  svg {
    height: 100%;
    position: absolute;
    bottom: 0px;
    right: 0px;
    transform: translate(1rem, 2rem);
  }
`;
